/**
 * The web glue between the suggestion plugin and the list (#1599 Story 1.4,
 * AC 4): one `div.ss-mention-suggester` per editor, positioned by the
 * plugin's own `props.mount(element)` (floating-ui, anchored at the trigger,
 * `top-start`, flips when there is no room, follows the trigger through
 * scroll and resize via `autoUpdate`), with `MentionSuggester`'s React rows
 * rendered into it through `createPortal` (the `LocationTagPicker.tsx`
 * portal pattern, `react-dom` required lazily).
 *
 * ⚠️ THE HANDLE IS STABLE AND READS REFS. The extension list that holds `ui`
 * is built once, at mount, so `ui` must be the same object for the life of
 * the editor, and `keyDown` must answer synchronously from the CURRENT items
 * and selection: it reads `liveRef`, never React state.
 *
 * A group row is picked like a person row (#1599 Story 1.7): Enter, Tab or
 * a click hands it to the plugin's `command`, which inserts the team token.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';

import {
  filterMentionCandidates,
  type MentionCandidate,
} from '../correspondence/mentionCandidates';
import { MentionSuggester } from '../correspondence/MentionSuggester';

import type {
  MentionSuggestSource,
  SuggesterUiHandle,
} from './siteScopeMention';
import type { SuggestionProps } from '@tiptap/suggestion';
import type { MutableRefObject, ReactNode } from 'react';

export const MENTION_SUGGESTER_CLASS = 'ss-mention-suggester';
/** Above the docked panel (the `LocationTagPicker` precedent). */
export const MENTION_SUGGESTER_Z_INDEX = 999999;

interface Live {
  element: HTMLDivElement;
  unmount: (() => void) | null;
  items: readonly MentionCandidate[];
  selected: number;
  query: string;
  command: (item: MentionCandidate) => void;
}

interface View {
  element: HTMLDivElement;
  items: readonly MentionCandidate[];
  selected: number;
}

const clamp = (index: number, length: number): number =>
  length === 0 ? 0 : Math.min(Math.max(index, 0), length - 1);

function getCreatePortal():
  | ((ui: ReactNode, container: Element) => ReactNode)
  | null {
  try {
    return require('react-dom').createPortal as (
      ui: ReactNode,
      container: Element,
    ) => ReactNode;
  } catch {
    return null;
  }
}

export interface MentionSuggesterHost {
  ui: SuggesterUiHandle;
  /** The open list, to render beside `<EditorContent>`; `null` when closed. */
  portal: ReactNode;
  /** Re-filter from the source (candidates or status changed while open). */
  refresh(): void;
}

export function useMentionSuggesterHost(
  source: MutableRefObject<MentionSuggestSource>,
): MentionSuggesterHost {
  const liveRef = useRef<Live | null>(null);
  const [view, setView] = useState<View | null>(null);

  const host = useMemo(() => {
    const publish = () => {
      const live = liveRef.current;
      setView(
        live
          ? {
              element: live.element,
              items: live.items,
              selected: live.selected,
            }
          : null,
      );
    };
    const pick = (item: MentionCandidate | undefined) => {
      const live = liveRef.current;
      if (!live || !item) return;
      live.command(item);
    };
    const close = () => {
      const live = liveRef.current;
      if (!live) return;
      liveRef.current = null;
      live.unmount?.();
      if (live.element.isConnected) live.element.remove();
      publish();
    };
    const ui: SuggesterUiHandle = {
      open: (props: SuggestionProps<MentionCandidate>) => {
        close();
        const element = document.createElement('div');
        element.className = MENTION_SUGGESTER_CLASS;
        element.style.zIndex = String(MENTION_SUGGESTER_Z_INDEX);
        liveRef.current = {
          element,
          unmount: null,
          items: props.items,
          selected: 0,
          query: props.query,
          command: item => props.command(item),
        };
        liveRef.current.unmount = props.mount(element);
        publish();
      },
      update: (props: SuggestionProps<MentionCandidate>) => {
        const live = liveRef.current;
        if (!live) return;
        // A new query re-filters the rows, so the highlight goes back to the
        // first one: an index kept across a narrowing would point at a
        // different person (review 2026-10-22).
        if (props.query !== live.query) live.selected = 0;
        live.items = props.items;
        live.query = props.query;
        live.command = item => props.command(item);
        live.selected = clamp(live.selected, live.items.length);
        publish();
      },
      keyDown: ({ event }) => {
        const live = liveRef.current;
        if (!live) return false;
        const count = live.items.length;
        switch (event.key) {
          // An empty list (still loading, or nothing to show) lets the
          // arrows move the caret: nothing on screen would explain an eaten
          // key (review 2026-10-22).
          case 'ArrowDown':
            if (count === 0) return false;
            live.selected = (live.selected + 1) % count;
            publish();
            return true;
          case 'ArrowUp':
            if (count === 0) return false;
            live.selected = (live.selected - 1 + count) % count;
            publish();
            return true;
          case 'Enter':
          case 'Tab':
            if (count === 0) return false;
            pick(live.items[live.selected]);
            return true;
          case 'Escape':
          case 'Esc':
            close();
            return true;
          default:
            return false;
        }
      },
      close,
    };
    return {
      ui,
      pick,
      hover: (index: number) => {
        const live = liveRef.current;
        if (!live || index === live.selected) return;
        live.selected = clamp(index, live.items.length);
        publish();
      },
      refresh: () => {
        const live = liveRef.current;
        if (!live) return;
        // The endpoint answered "mentions are off" while the list was open:
        // close it rather than say "No one to mention here".
        if (!source.current.enabled) {
          close();
          return;
        }
        live.items = filterMentionCandidates(
          source.current.candidates,
          live.query,
        );
        live.selected = clamp(live.selected, live.items.length);
        publish();
      },
    };
  }, [source]);

  // Read inside the hook body, never at module scope (`correspondence.md`).
  const createPortal = Platform.OS === 'web' ? getCreatePortal() : null;
  const portal =
    view && createPortal
      ? createPortal(
          <MentionSuggester
            items={view.items}
            selectedIndex={view.selected}
            status={source.current.status}
            onPick={host.pick}
            onHover={host.hover}
          />,
          view.element,
        )
      : null;

  return { ui: host.ui, portal, refresh: host.refresh };
}
